/**
 * Shared Bean physics engine.
 * Used by both the marketing site and preview page widget.
 *
 * Provides: LPF movement, velocity tracking, exhaust particles, sparkles.
 * Does NOT provide: DOM elements, state machines, chat, tooltips.
 * Consumers wire their own state machines on top.
 */

export var EXHAUST_COLORS = ['#e84393', '#f472b6', '#38bdf8', '#fbbf24', '#a78bfa', '#fff', '#34d399'];

// ── Paw SVG (shared) — served from makeoverexpert.nl, works for both the
// marketing site and the mockups-inject widget loaded on client pages. ──
export var PAW_URL = "https://makeoverexpert.nl/images/dog-paw.svg";

/**
 * Core physics engine. Tracks position, target, velocity via exponential decay LPF.
 *
 * Usage:
 *   var engine = new BeanEngine({ size: 48, speed: 3.5 });
 *   engine.onTick(function(e) {
 *     el.style.transform = 'translate(' + e.x + 'px,' + e.y + 'px) scale(' + e.scale + ')';
 *   });
 *   engine.onArrival(function() { ... });
 *   engine.setTarget(100, 200);
 *   engine.start();
 */
export function BeanEngine(config) {
	config = config || {};
	this.size = config.size || 48;
	this.speed = config.speed || 3.5;
	this.arrivalThreshold = config.arrivalThreshold || 3;
	this.x = 0;
	this.y = 0;
	this.targetX = 0;
	this.targetY = 0;
	this.scale = config.initialScale || 1;
	this.targetScale = config.initialScale || 1;
	this.vel = 0;
	this.speedMultiplier = 1;
	this._lastTime = 0;
	this._running = false;
	this._arrivalCb = null;
	this._tickCb = null;
	this._boundTick = this._tick.bind(this);
}

BeanEngine.prototype.setTarget = function(x, y, scale) {
	this.targetX = x;
	this.targetY = y;
	if (scale !== undefined) this.targetScale = scale;
};

BeanEngine.prototype.setPosition = function(x, y, scale) {
	this.x = x;
	this.y = y;
	this.targetX = x;
	this.targetY = y;
	if (scale !== undefined) { this.scale = scale; this.targetScale = scale; }
};

BeanEngine.prototype.onTick = function(cb) { this._tickCb = cb; };
BeanEngine.prototype.onArrival = function(cb) { this._arrivalCb = cb; };

BeanEngine.prototype.start = function() {
	if (this._running) return;
	this._running = true;
	this._lastTime = 0;
	requestAnimationFrame(this._boundTick);
};

BeanEngine.prototype.stop = function() {
	this._running = false;
};

BeanEngine.prototype._tick = function(now) {
	if (!this._running) return;
	requestAnimationFrame(this._boundTick);

	if (!this._lastTime) { this._lastTime = now; return; }
	var dt = Math.min((now - this._lastTime) / 1000, 0.1);
	this._lastTime = now;

	// Exponential decay LPF
	var speed = this.speed * this.speedMultiplier;
	var lpf = 1 - Math.exp(-speed * dt);
	var dx = this.targetX - this.x;
	var dy = this.targetY - this.y;
	this.x += dx * lpf;
	this.y += dy * lpf;
	this.scale += (this.targetScale - this.scale) * lpf;
	this.vel = Math.hypot(dx, dy) * speed;

	// Notify consumer
	if (this._tickCb) {
		this._tickCb({
			x: Math.round(this.x * 10) / 10,
			y: Math.round(this.y * 10) / 10,
			scale: Math.round(this.scale * 100) / 100,
			vel: this.vel,
			dt: dt,
		});
	}

	// Arrival detection
	var dist = Math.hypot(this.targetX - this.x, this.targetY - this.y);
	if (dist < this.arrivalThreshold && this._arrivalCb) {
		var cb = this._arrivalCb;
		this._arrivalCb = null;
		cb();
	}
};

/**
 * Create an exhaust particle DOM element.
 * Append to your container, it auto-removes on animation end.
 *
 * @param {Object} config
 * @param {number} config.cx - center x (relative to container)
 * @param {number} config.bottom - bottom y (relative to container)
 * @param {string} [config.className] - CSS class prefix ('me-' or 'firework-')
 * @param {number} [config.pawChance] - probability of paw (default 0.04)
 */
export function createParticle(config) {
	var prefix = config.className || 'me-';
	var isPaw = Math.random() < (config.pawChance || 0.04);
	var p = document.createElement('div');
	p.className = prefix + 'particle ' + prefix + (isPaw ? 'paw' : 'dot');
	var sz = isPaw ? (5 + Math.random() * 8) : (2 + Math.random() * 4);
	p.style.width = sz + 'px';
	p.style.height = sz + 'px';
	if (!isPaw) p.style.background = EXHAUST_COLORS[Math.floor(Math.random() * EXHAUST_COLORS.length)];
	p.style.left = (config.cx + (Math.random() - 0.5) * (config.spread || 20)) + 'px';
	p.style.top = config.bottom + 'px';
	var dist = isPaw ? (40 + Math.random() * 80) : (25 + Math.random() * 50);
	p.style.setProperty('--fx', ((Math.random() - 0.5) * (isPaw ? 60 : 40)) + 'px');
	p.style.setProperty('--fy', dist + 'px');
	p.style.setProperty('--fr', (Math.random() * 720 - 360) + 'deg');
	var dur = isPaw ? (0.5 + Math.random() * 0.5) : (0.3 + Math.random() * 0.3);
	p.style.setProperty('--fw-dur', dur + 's');
	p.style.setProperty('--dur', dur + 's');
	p.style.setProperty('--fw-delay', '0s');
	p.addEventListener('animationend', function() { this.remove(); });
	return p;
}

/**
 * Make bean image(s) blink at random intervals by swapping img.src
 * between the original and a blink (eyes-closed) frame. Multiple imgs
 * passed together blink in sync — useful when several Bean avatars
 * appear on the same page.
 *
 * @param {Element|NodeList|Array} imgs - one or more <img> elements
 * @param {string} blinkSrc - URL of the eyes-closed frame
 * @param {Object} [opts]
 * @param {number} [opts.minInterval=3500] - min ms between blinks
 * @param {number} [opts.maxInterval=7500] - max ms between blinks
 * @param {number} [opts.minDuration=70] - min ms eyes stay closed
 * @param {number} [opts.maxDuration=110] - max ms eyes stay closed
 * @param {number} [opts.doubleChance=0.25] - chance of a double-blink
 * @returns {{stop: function, pause: function, resume: function, setClosed: function, setOpen: function}}
 */
export function startBlinking(imgs, blinkSrc, opts) {
	opts = opts || {};
	var minInterval = opts.minInterval || 3500;
	var maxInterval = opts.maxInterval || 7500;
	var minDuration = opts.minDuration || 70;
	var maxDuration = opts.maxDuration || 110;
	var doubleChance = opts.doubleChance != null ? opts.doubleChance : 0.25;

	var list = [];
	if (imgs) {
		if (imgs.tagName === 'IMG') list.push(imgs);
		else if (imgs.length !== undefined) {
			for (var i = 0; i < imgs.length; i++) {
				if (imgs[i] && imgs[i].tagName === 'IMG') list.push(imgs[i]);
			}
		}
	}
	var noop = function() {};
	if (!list.length) return { stop: noop, pause: noop, resume: noop, setClosed: noop, setOpen: noop };

	// Capture each image's original src so we can restore it per-image
	var originals = [];
	for (var j = 0; j < list.length; j++) originals.push(list[j].src);

	// Preload blink frame to avoid first-blink flicker
	var preload = new Image();
	preload.src = blinkSrc;

	var stopped = false;
	var paused = false;
	var timer = null;

	function setBlink() {
		for (var k = 0; k < list.length; k++) list[k].src = blinkSrc;
	}
	function setOpenImpl() {
		for (var k = 0; k < list.length; k++) list[k].src = originals[k];
	}

	function blinkOnce(remaining) {
		if (stopped || paused) return;
		setBlink();
		var dur = minDuration + Math.random() * (maxDuration - minDuration);
		timer = setTimeout(function() {
			if (stopped || paused) return;
			setOpenImpl();
			if (remaining > 1) {
				timer = setTimeout(function() { blinkOnce(remaining - 1); }, 90 + Math.random() * 80);
			} else {
				scheduleNext();
			}
		}, dur);
	}

	function scheduleNext() {
		if (stopped || paused) return;
		var delay = minInterval + Math.random() * (maxInterval - minInterval);
		timer = setTimeout(function() {
			blinkOnce(Math.random() < doubleChance ? 2 : 1);
		}, delay);
	}

	scheduleNext();

	return {
		stop: function() {
			stopped = true;
			if (timer) { clearTimeout(timer); timer = null; }
			setOpenImpl();
		},
		pause: function() {
			paused = true;
			if (timer) { clearTimeout(timer); timer = null; }
			setOpenImpl();
		},
		resume: function() {
			if (stopped || !paused) return;
			paused = false;
			setOpenImpl();
			scheduleNext();
		},
		setClosed: setBlink,
		setOpen: setOpenImpl,
	};
}

/**
 * Typing animator: rapidly cycles bean img(s) through a set of "typing"
 * frames to signal activity (Bean typing a message, thinking, working).
 *
 * Frames may be plain URLs (treated as both-paws-down rest poses) or
 * objects of the form { url, leftUp, rightUp } where leftUp/rightUp tag
 * which paws are off the keyboard. The picker enforces a no-up-twice-in-
 * a-row rule per paw, so each paw is always either resting (down) or
 * actively striking (down→up or up→down). It will never sit hovering
 * across two consecutive frames.
 *
 * If a blinker is passed in opts, start() pauses it (which forces rest) and
 * stop() resumes it. The rest baseline is captured at start() from img.src,
 * so the blinker must already have restored the rest frame by then — that's
 * why blinker.pause() calls setOpen() internally.
 *
 * @param {Element|NodeList|Array} imgs - one or more <img> elements
 * @param {Array<string|{url: string, leftUp?: boolean, rightUp?: boolean}>} frames
 * @param {Object} [opts]
 * @param {number} [opts.minFrameMs=93]  - shortest ms between frame swaps
 * @param {number} [opts.maxFrameMs=140] - longest ms between frame swaps
 * @param {Object} [opts.blinker]        - blinker instance from startBlinking
 * @returns {{start: function, stop: function}}
 */
export function createTypingAnimator(imgs, frames, opts) {
	opts = opts || {};
	var minFrameMs = opts.minFrameMs != null ? opts.minFrameMs : 93;
	var maxFrameMs = opts.maxFrameMs != null ? opts.maxFrameMs : 140;
	var blinker = opts.blinker || null;

	var list = [];
	if (imgs) {
		if (imgs.tagName === 'IMG') list.push(imgs);
		else if (imgs.length !== undefined) {
			for (var i = 0; i < imgs.length; i++) {
				if (imgs[i] && imgs[i].tagName === 'IMG') list.push(imgs[i]);
			}
		}
	}
	var noop = function() {};
	if (!list.length || !frames || !frames.length) return { start: noop, stop: noop };

	// Normalize frames: each entry becomes { url, leftUp, rightUp }
	var normFrames = [];
	for (var nf = 0; nf < frames.length; nf++) {
		var fr = frames[nf];
		if (typeof fr === 'string') {
			normFrames.push({ url: fr, leftUp: false, rightUp: false });
		} else {
			normFrames.push({
				url: fr.url,
				leftUp: !!fr.leftUp,
				rightUp: !!fr.rightUp,
			});
		}
	}

	// Preload all frames — store the Image refs in a closure-local array so
	// they're never garbage-collected. Without this, rapid img.src swaps on
	// the real <img> elements cancel in-flight fetches because the cache
	// hasn't been populated yet (only visible when the server returns
	// must-revalidate, but safer to do regardless).
	var preloads = [];
	for (var f = 0; f < normFrames.length; f++) {
		var pre = new Image();
		pre.src = normFrames[f].url;
		preloads.push(pre);
	}

	var running = false;
	var timer = null;
	var lastFrame = -1;
	var rests = [];

	function setSrc(src) {
		for (var i = 0; i < list.length; i++) list[i].src = src;
	}

	function nextDur() {
		return minFrameMs + Math.random() * (maxFrameMs - minFrameMs);
	}

	// Per-paw rule: a paw can never be "up" in two consecutive frames.
	// Allowed transitions for each paw: up→down, down→up, down→down.
	function validNext(prev, cand) {
		if (!prev) return true;
		if (prev.leftUp && cand.leftUp) return false;
		if (prev.rightUp && cand.rightUp) return false;
		return true;
	}

	// Pick a random frame that satisfies the constraint and isn't the
	// immediately-previous frame. Falls back to any non-repeat if no
	// constraint-valid candidates exist (shouldn't happen with a sensible
	// frame set, but keeps the loop alive).
	function pickFrame() {
		if (normFrames.length === 1) return 0;
		var prev = lastFrame >= 0 ? normFrames[lastFrame] : null;
		var valid = [];
		for (var i = 0; i < normFrames.length; i++) {
			if (i === lastFrame) continue;
			if (validNext(prev, normFrames[i])) valid.push(i);
		}
		var pick;
		if (valid.length > 0) {
			pick = valid[Math.floor(Math.random() * valid.length)];
		} else {
			pick = Math.floor(Math.random() * normFrames.length);
			if (pick === lastFrame) pick = (pick + 1) % normFrames.length;
		}
		lastFrame = pick;
		return pick;
	}

	function tick() {
		if (!running) return;
		setSrc(normFrames[pickFrame()].url);
		timer = setTimeout(tick, nextDur());
	}

	return {
		start: function() {
			if (running) return;
			if (blinker) blinker.pause();
			// Capture the current (rest) src per-image as our baseline
			rests = [];
			for (var i = 0; i < list.length; i++) rests.push(list[i].src);
			running = true;
			lastFrame = -1;
			// Kick off immediately so there's no delay before the first swap
			setSrc(normFrames[pickFrame()].url);
			timer = setTimeout(tick, nextDur());
		},
		stop: function() {
			if (!running) return;
			running = false;
			if (timer) { clearTimeout(timer); timer = null; }
			for (var i = 0; i < list.length; i++) list[i].src = rests[i];
			if (blinker) blinker.resume();
		},
	};
}

/**
 * Sequence animator: plays a linear sequence of frames exactly once, then
 * restores the rest frame. Used for play-once idle animations (e.g. Bean
 * taking a drink of coffee) rather than the looping typing/blink states.
 *
 * Coordinates with an optional blinker: start() pauses it (forcing rest),
 * end restores it. Caller is responsible for gating .play() so it doesn't
 * stomp on other active states (typing, tooltip flight, etc.).
 *
 * @param {Element|NodeList|Array} imgs - one or more <img> elements
 * @param {string[]} frames - URLs to play in order
 * @param {Object} [opts]
 * @param {number} [opts.frameMs=200]     - ms each frame is shown
 * @param {number} [opts.holdLastMs=frameMs] - extra ms to hold the final frame
 * @param {number} [opts.apexHoldMin=0]   - min ms to hold at the apex (middle) frame
 * @param {number} [opts.apexHoldMax=0]   - max ms to hold at the apex frame
 * @param {Object} [opts.blinker]         - blinker instance from startBlinking
 * @returns {{play: function, stop: function, isPlaying: function}}
 */
export function createSequenceAnimator(imgs, frames, opts) {
	opts = opts || {};
	var frameMs = opts.frameMs || 200;
	var holdLastMs = opts.holdLastMs != null ? opts.holdLastMs : frameMs;
	var apexHoldMin = opts.apexHoldMin || 0;
	var apexHoldMax = opts.apexHoldMax || 0;
	var blinker = opts.blinker || null;

	var list = [];
	if (imgs) {
		if (imgs.tagName === 'IMG') list.push(imgs);
		else if (imgs.length !== undefined) {
			for (var i = 0; i < imgs.length; i++) {
				if (imgs[i] && imgs[i].tagName === 'IMG') list.push(imgs[i]);
			}
		}
	}
	var noop = function() {};
	var never = function() { return false; };
	if (!list.length || !frames || !frames.length) return { play: noop, stop: noop, isPlaying: never };

	// Preload all frames — keep refs alive to prevent GC so the cache is warm
	var preloads = [];
	for (var f = 0; f < frames.length; f++) {
		var pre = new Image();
		pre.src = frames[f];
		preloads.push(pre);
	}

	var running = false;
	var timer = null;
	var rests = [];
	var onDoneCb = null;

	function setSrc(src) {
		for (var i = 0; i < list.length; i++) list[i].src = src;
	}
	function restore() {
		for (var i = 0; i < list.length; i++) list[i].src = rests[i];
	}

	return {
		play: function(onDone) {
			if (running) return;
			if (blinker) blinker.pause();
			rests = [];
			for (var i = 0; i < list.length; i++) rests.push(list[i].src);
			running = true;
			onDoneCb = onDone || null;
			var idx = 0;
			var apexIdx = Math.floor(frames.length / 2);
			function step() {
				if (!running) return;
				setSrc(frames[idx]);
				idx++;
				if (idx < frames.length) {
					// Hold at the apex (midpoint) frame for a random sip duration
					var delay = (idx - 1 === apexIdx && apexHoldMax > 0)
						? frameMs + apexHoldMin + Math.random() * (apexHoldMax - apexHoldMin)
						: frameMs;
					timer = setTimeout(step, delay);
				} else {
					// Hold the final frame briefly, then restore & resume
					timer = setTimeout(function() {
						if (!running) return;
						restore();
						running = false;
						if (blinker) blinker.resume();
						var cb = onDoneCb;
						onDoneCb = null;
						if (cb) cb();
					}, holdLastMs);
				}
			}
			step();
		},
		stop: function() {
			if (!running) return;
			running = false;
			if (timer) { clearTimeout(timer); timer = null; }
			restore();
			if (blinker) blinker.resume();
			var cb = onDoneCb;
			onDoneCb = null;
			if (cb) cb();
		},
		isPlaying: function() { return running; },
	};
}

/**
 * Create a sparkle DOM element.
 * Append to your container, it auto-removes on animation end.
 *
 * @param {Object} config
 * @param {number} config.cx - center x
 * @param {number} config.cy - center y
 * @param {number} config.radius - spawn radius from center
 * @param {string} [config.className] - CSS class ('me-sparkle' or 'sparkle')
 */
export function createSparkle(config) {
	var s = document.createElement('div');
	s.className = config.className || 'me-sparkle';
	var angle = Math.random() * Math.PI * 2;
	var dist = (config.radius || 24) + Math.random() * 12;
	s.style.left = (config.cx + Math.cos(angle) * dist) + 'px';
	s.style.top = (config.cy + Math.sin(angle) * dist) + 'px';
	s.style.width = (3 + Math.random() * 4) + 'px';
	s.style.height = s.style.width;
	s.style.background = EXHAUST_COLORS[Math.floor(Math.random() * EXHAUST_COLORS.length)];
	s.style.boxShadow = '0 0 6px ' + s.style.background;
	s.style.setProperty('--dx', (Math.random() - 0.5) * 40 + 'px');
	s.style.setProperty('--dy', -(15 + Math.random() * 30) + 'px');
	s.addEventListener('animationend', function() { this.remove(); });
	return s;
}

/**
 * Shared keyframe CSS. Consumers inject this into their styles.
 * Uses CSS custom properties for animation parameters.
 */
export var PHYSICS_CSS = '\
@keyframes me-burst{0%{opacity:1;transform:translate(0,0) scale(1) rotate(0deg)}60%{opacity:1}100%{opacity:0;transform:translate(var(--fx),var(--fy)) scale(0.2) rotate(var(--fr))}}\
@keyframes me-sparkle-float{0%{opacity:1;transform:scale(1) translate(0,0)}100%{opacity:0;transform:scale(0) translate(var(--dx,0),var(--dy,-40px))}}\
@keyframes me-vibrate{0%,100%{transform:translate(0,0) rotate(0)}10%{transform:translate(-1px,0.6px) rotate(-0.6deg)}20%{transform:translate(1px,-0.5px) rotate(0.5deg)}30%{transform:translate(-0.8px,-0.6px) rotate(-0.5deg)}40%{transform:translate(0.6px,0.8px) rotate(0.6deg)}50%{transform:translate(-0.5px,-0.5px) rotate(-0.3deg)}60%{transform:translate(0.8px,0.3px) rotate(0.5deg)}70%{transform:translate(-0.6px,0.5px) rotate(-0.5deg)}80%{transform:translate(0.5px,-0.6px) rotate(0.3deg)}90%{transform:translate(-0.3px,0.4px) rotate(-0.3deg)}}\
@keyframes me-bean-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}\
.me-particle{position:absolute;pointer-events:none}\
.me-dot{border-radius:50%;animation:me-burst var(--dur,0.8s) cubic-bezier(0,0.6,0.4,1) forwards;opacity:0}\
.me-paw{background:url("' + PAW_URL + '") center/contain no-repeat;animation:me-burst var(--dur,1.2s) cubic-bezier(0,0.6,0.4,1) forwards;opacity:0}\
.me-sparkle{position:absolute;border-radius:50%;pointer-events:none;animation:me-sparkle-float 1.2s ease-out forwards}\
';
